import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { User, Crown, Clapperboard as ClapIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { COUNTRY_LIST } from "@/lib/constants";

const ROLES = [
  { id: "FREE_USER", label: "Free Viewer", desc: "Browse & watch free content", icon: User },
  { id: "SUBSCRIBER", label: "Premium Fan", desc: "Unlock all films & series", icon: Crown },
  { id: "CREATOR", label: "Creator", desc: "Upload & monetise your work", icon: ClapIcon },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("FREE_USER");
  const [form, setForm] = useState({ name: "", email: "", password: "", display_name: "", bio: "", country: "ZA" });
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ ...form, role });
      toast.success("Account created! Welcome to Tazama Africa 🎬");
      navigate(role === "CREATOR" ? "/creator/dashboard" : role === "SUBSCRIBER" ? "/subscriptions" : "/dashboard");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(212,175,55,0.1),transparent_50%)]" />
      <div className="relative w-full max-w-lg glass rounded-2xl p-8 animate-fade-up">
        <div className="flex items-center gap-2 mb-6">
          <img src="/logo-mark.png" alt="Tazama Africa logo" className="w-10 h-10 rounded-xl object-cover" />
          <span className="font-display font-extrabold text-xl">Tazama <span className="text-[#E50914]">Africa</span></span>
        </div>
        <h1 className="font-display font-black text-3xl mb-1">Join Tazama Africa</h1>
        <p className="text-white/50 text-sm mb-6">Choose how you want to experience African cinema.</p>

        <div className="grid grid-cols-3 gap-2 mb-6">
          {ROLES.map((r) => (
            <button key={r.id} onClick={() => setRole(r.id)} data-testid={`role-${r.id}`}
              className={`p-3 rounded-xl border text-left transition-all ${role === r.id
                ? "border-[#E50914] bg-[#E50914]/10" : "border-white/10 bg-white/5 hover:border-white/20"}`}>
              <r.icon className={`w-5 h-5 mb-2 ${role === r.id ? "text-[#E50914]" : "text-white/60"}`} />
              <p className="text-xs font-semibold leading-tight">{r.label}</p>
              <p className="text-[10px] text-white/40 mt-0.5 leading-tight">{r.desc}</p>
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          <Field label="Full name" testid="reg-name" value={form.name} onChange={upd("name")} required />
          <Field label="Email" type="email" testid="reg-email" value={form.email} onChange={upd("email")} required />
          <Field label="Password" type="password" testid="reg-password" value={form.password} onChange={upd("password")} required />

          {role === "CREATOR" && (
            <div className="space-y-4 p-4 rounded-xl bg-[#D4AF37]/5 border border-[#D4AF37]/20">
              <p className="text-xs uppercase tracking-wider text-[#D4AF37]">Creator profile</p>
              <Field label="Studio / display name" testid="reg-display-name" value={form.display_name} onChange={upd("display_name")} />
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Country</label>
                <select value={form.country} onChange={upd("country")} data-testid="reg-country"
                  className="w-full h-11 px-4 rounded-lg bg-white/5 border border-white/10 focus:border-[#E50914]/60 focus:outline-none">
                  {COUNTRY_LIST.map((c) => <option key={c.code} value={c.code} className="bg-[#1A1724]">{c.flag} {c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Bio</label>
                <textarea value={form.bio} onChange={upd("bio")} rows={2} data-testid="reg-bio"
                  className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-[#E50914]/60 focus:outline-none resize-none" />
              </div>
            </div>
          )}

          <button data-testid="register-submit" disabled={loading}
            className="w-full h-11 rounded-lg bg-[#E50914] hover:bg-[#FF2A3B] font-semibold transition-colors disabled:opacity-60">
            {loading ? "Creating…" : "Create account"}
          </button>
        </form>

        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-white/10" /><span className="text-xs text-white/40">or</span><div className="flex-1 h-px bg-white/10" />
        </div>
        <button onClick={googleLogin} data-testid="register-google"
          className="w-full h-11 rounded-lg bg-white text-black font-semibold flex items-center justify-center gap-2 hover:bg-white/90 transition-colors">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" /> Continue with Google
        </button>
        <p className="text-center text-sm text-white/50 mt-6">
          Already have an account? <Link to="/login" className="text-[#E50914] font-medium hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}

const Field = ({ label, testid, ...props }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">{label}</label>
    <input data-testid={testid} {...props}
      className="w-full h-11 px-4 rounded-lg bg-white/5 border border-white/10 focus:border-[#E50914]/60 focus:outline-none transition-colors" />
  </div>
);
